import { memo, useEffect } from 'react';
import { useBoolean } from 'ahooks';
import { Button, Form } from 'antd';
import { Store } from 'antd/es/form/interface';

import { useMemoState } from 'hooks';

import { IInitialTableActions, IMilesTableProps, InitialTableActions, TEditableColumn } from 'types';

import { CRUDTableCell } from './CRUDTableCell';
import {
  getClassName,
  getColumnsWithActions,
  handleAddRow,
  handleScrollToLastRow,
  handleValidateOnChange,
} from './lib';

import { MilesTable } from '../MilesTable';

import './CRUDTable.styles.scss';

const { useForm } = Form;

interface ICRUDTableProps<T> extends Omit<IMilesTableProps<T>, 'columns'> {
  addNewBtn?: {
    text?: string;
    visible: boolean;
  };
  columns: TEditableColumn<T>[];
  initialValues?: Store;
  initialTableActions?: IInitialTableActions[];
  getRowClassName?: (data: T, editingRowKey?: React.Key) => string;
  onPostTableData?: (data: T) => void;
  onPatchTableData?: (data: T, actionType?: InitialTableActions) => void;
  onDeleteTableData?: (id: number) => void;
}

const CRUDTable = <T extends Record<string, unknown>, >({
  id,
  title,
  rowKey,
  columns,
  loading,
  children,
  addNewBtn,
  pagination,
  dataSource,
  rowClassName,
  initialValues,
  titleExtraNode,
  verticalScroll,
  horizontalScroll = true,
  initialTableActions,
  onChange,
  getRowClassName,
  onPostTableData,
  onPatchTableData,
  onDeleteTableData,
}: ICRUDTableProps<T>) => {
  const [form] = useForm<T>();

  const [editingRowKey, setEditingKey] = useMemoState<number>();
  const [isFormValid, { set: setIsFormValid }] = useBoolean(true);
  const [editableTableData, setEditableTableData] = useMemoState<T[]>([]);

  const isEditOrNewRow = !!editingRowKey || editingRowKey === 0;

  useEffect(() => {
    const dataSourceWithKeys = dataSource?.map((item) => ({
      ...item,
      key: item.id,
    }));

    setEditableTableData(dataSourceWithKeys);
  }, [dataSource]);

  useEffect(() => {
    handleValidateOnChange(form, setIsFormValid)();
    handleScrollToLastRow();
  }, [editingRowKey]);

  const columnsWithActions = getColumnsWithActions({
    form,
    columns,
    isFormValid,
    editingRowKey,
    editableTableData,
    initialTableActions,
    setEditingKey,
    setIsFormValid,
    onPostTableData,
    onPatchTableData,
    onDeleteTableData,
    setEditableTableData,
  });

  return (
    <Form
      form={form}
      component={false}
      initialValues={initialValues}
      onValuesChange={handleValidateOnChange(form, setIsFormValid)}
    >
      <MilesTable
        id={id}
        title={title}
        rowKey={rowKey}
        columns={columnsWithActions}
        loading={loading}
        pagination={pagination}
        dataSource={editableTableData}
        titleExtraNode={titleExtraNode}
        verticalScroll={verticalScroll}
        horizontalScroll={horizontalScroll}
        onChange={onChange}
        rowClassName={getClassName(
          rowClassName,
          editingRowKey,
          getRowClassName,
        )}
        underTableNode={(onPostTableData && addNewBtn?.visible) && (
          <Button
            type='link'
            size='large'
            disabled={isEditOrNewRow}
            onClick={handleAddRow({
              columns,
              setEditingKey,
              setEditableTableData,
            })}
          >
            {addNewBtn.text || 'Add New'}
          </Button>
        )}
        components={{
          body: {
            cell: CRUDTableCell,
          },
        }}
      />

      {children}
    </Form>
  );
};

export default memo(CRUDTable);
